import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { Box, Checkbox, DropdownFilter, FormGroup } from '@ksyos/design-system';
import { useCallback, useMemo } from 'react';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type { OutpatientClinicTaskListFilter } from '@frontend/components/pages/outpatientClinic/types/outpatientClinicTaskListFilter';
import { getSelectedLanguage } from '@frontend/translate';
import type { OutpatientClinicCategory } from '../../types/outpatientClinicCategory';
import { useGroupedCategories } from '../../hooks/useGroupedCategories';
import { translateOutpatientClinicCategory } from '../../utils/translateOutpatientClinicCategory';

export type CategoryFilterProps = {
    filter: OutpatientClinicTaskListFilter;
    localCategory: OutpatientClinicTaskListFilter['category'];
    setFilter: (filter: OutpatientClinicTaskListFilter) => void;
    setLocalCategory: Dispatch<SetStateAction<OutpatientClinicTaskListFilter['category']>>;
    types: OutpatientClinicTask['type'][];
};

export function CategoryFilter({ filter, localCategory, setFilter, setLocalCategory, types }: CategoryFilterProps) {
    const translate = getSelectedLanguage();
    const groupedCategories = useGroupedCategories(types);

    const handleReset = useCallback(() => {
        setLocalCategory(undefined);
        setFilter({ ...filter, category: undefined });
    }, [filter, setLocalCategory, setFilter]);

    const handleSubmit = useCallback(() => {
        setFilter({
            ...filter,
            category: localCategory,
        });
    }, [filter, localCategory, setFilter]);

    const handleCheckboxChange = useCallback(
        (categoryId: string) => (event: ChangeEvent<HTMLInputElement>) => {
            setLocalCategory((prev) =>
                event.target.checked
                    ? [...(prev || []), { id: categoryId }]
                    : (prev || []).filter((category) => category.id !== categoryId)
            );
        },
        [setLocalCategory]
    );

    const selectedCategories = useMemo(
        () => new Set((localCategory || []).map((category) => category.id)),
        [localCategory]
    );

    const categoryCheckboxes = useMemo(
        () =>
            groupedCategories.map((category) => (
                <Checkbox
                    checked={selectedCategories.has(category.id)}
                    key={category.id}
                    label={translateOutpatientClinicCategory(category.name as OutpatientClinicCategory)}
                    onChange={handleCheckboxChange(category.id)}
                />
            )),
        [groupedCategories, handleCheckboxChange, selectedCategories]
    );

    return (
        <FormGroup label={translate.outpatientClinic.tasks.category}>
            <DropdownFilter
                count={filter.category?.length}
                label={translate.common.filter}
                onReset={handleReset}
                onSubmit={handleSubmit}
                width="full"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    {categoryCheckboxes}
                </Box>
            </DropdownFilter>
        </FormGroup>
    );
}
